import {RelativeTime} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {CheckCircleFillIcon, CheckCircleIcon, XCircleFillIcon} from '@primer/octicons-react'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {SidebarDeploymentsProps, DeploymentState} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarListItem} from './SidebarListItem'
import styles from './SidebarDeployments.module.css'

function DeploymentStatusIcon({state}: {state: DeploymentState}) {
  switch (state) {
    case 'active':
      return <CheckCircleFillIcon className={styles.statusSuccess} />
    case 'error':
    case 'failure':
      return <XCircleFillIcon className={styles.statusDanger} />
    case 'inactive':
    case 'destroyed':
    case 'abandoned':
      return <CheckCircleIcon className={styles.statusMuted} />
    case 'in_progress':
      return <CheckCircleFillIcon className={styles.statusWarning} />
    case 'queued':
    case 'pending':
    case 'waiting':
      return <CheckCircleFillIcon className={styles.statusWarning} />
    default:
      return <CheckCircleIcon className={styles.statusMuted} />
  }
}

function getDeploymentStatus(env: {state: string; createdAt?: string}) {
  if (env.state === 'active' && env.createdAt) {
    return <RelativeTime date={new Date(env.createdAt)} tense="past" />
  }
  if (env.state === 'inactive') return <span>inactive</span>
  if (env.state === 'queued' || env.state === 'pending') return <span>queued</span>
  return null
}

/** Renders the Deployments section of the repository sidebar. */
export function SidebarDeployments({environments, deploymentsPath}: SidebarDeploymentsProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const totalDeployments = environments.length
  const baseMaxToShow = 3
  const maxToShow =
    totalDeployments === baseMaxToShow + 1 ? totalDeployments : Math.min(totalDeployments, baseMaxToShow)
  const displayed = environments.slice(0, maxToShow)

  const moreCount = totalDeployments - maxToShow

  return (
    <SidebarSection
      title="Deployments"
      href={deploymentsPath}
      count={totalDeployments}
      moreCount={moreCount > 1 ? moreCount : undefined}
      moreLabel={`deployment${moreCount === 1 ? '' : 's'}`}
    >
      {totalDeployments === 0 ? (
        <div className={styles.emptyState}>
          <span>No deployments published</span>
        </div>
      ) : (
        <ul className={styles.envList}>
          {displayed.map(env => (
            <SidebarListItem
              key={`deploy-${env.name}`}
              className={styles.envItem}
              icon={<DeploymentStatusIcon state={env.state} />}
              label={env.name}
              description={getDeploymentStatus(env)}
              href={env.path}
              onClick={() =>
                sendClickAnalyticsEvent({
                  category: 'REPOSITORY_OVERVIEW',
                  action: 'click',
                  label: 'location:sidebar;section:deployments',
                })
              }
            />
          ))}
        </ul>
      )}
    </SidebarSection>
  )
}

export function SidebarDeploymentsSkeleton({environmentNames}: {environmentNames?: string[]}) {
  if (environmentNames && environmentNames.length > 0) {
    return (
      <SidebarSection title="Deployments" count={environmentNames.length}>
        <ul className={styles.envList}>
          {environmentNames.map(name => (
            <li key={`deploy-skeleton-${name}`} className={styles.envItem}>
              <span className={styles.skeletonRow}>
                <span className={styles.skeletonLabel}>{name}</span>
                <SkeletonText size="bodySmall" />
              </span>
            </li>
          ))}
        </ul>
      </SidebarSection>
    )
  }

  return (
    <SidebarSection title="Deployments">
      <SkeletonText />
    </SidebarSection>
  )
}
